import { useState, useEffect } from 'react';
import { usePlayers } from '../../hooks/usePlayers';
import { useUser } from '../../hooks/useUser';
import { supabase } from '../../utils/supabase';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { Team } from '../../types';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

export function OnboardingModal({ isOpen, onComplete }: OnboardingModalProps) {
  const { players, loading: playersLoading, error: playersError } = usePlayers();
  const { user, setSelectedPlayer } = useUser();
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch teams
  useEffect(() => {
    async function fetchTeams() {
      if (!isOpen) return;
      
      setTeamsLoading(true);
      try {
        // Resolve the active season so we only show current-season teams
        // (avoids cross-season duplicates).
        const { data: activeSeason } = await supabase.rpc('get_active_season');

        let query = supabase
          .from('teams')
          .select('team_id, team_name, team_number, season_year')
          .not('team_id', 'is', null)
          .gt('team_id', 0);

        if (typeof activeSeason === 'number') {
          query = query.eq('season_year', activeSeason);
        }

        const { data, error } = await query.order('team_number');

        if (error) throw error;
        setTeams(data || []);
      } catch (err) {
        console.error('Error fetching teams:', err);
        setError('Failed to load teams');
      } finally {
        setTeamsLoading(false);
      }
    }

    fetchTeams();
  }, [isOpen]);

  const handleSave = async () => {
    if (!selectedPlayerId || !selectedTeamId) {
      setError('Please select both a player and a team');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Find the selected player
      const selectedPlayer = players.find(p => p.bowler_id.toString() === selectedPlayerId);
      if (!selectedPlayer) {
        throw new Error('Selected player not found');
      }

      // Parse and validate team ID
      const teamId = parseInt(selectedTeamId);
      if (isNaN(teamId)) {
        throw new Error('Invalid team selection');
      }

      // Derive the stable team number so the preference survives season changes.
      const selectedTeamNumber = teams.find(t => t.team_id === teamId)?.team_number ?? null;

      console.log('Saving onboarding selections:', {
        user_id: user?.id,
        selected_player_id: selectedPlayer.bowler_id,
        team_id: teamId,
        preferred_team_number: selectedTeamNumber,
        team_manually_set: true
      });

      // Update user preferences with both player and team
      // First, try to update existing record
      const { data: updateData, error: updateError } = await supabase
        .from('user_preferences')
        .update({
          selected_player_id: selectedPlayer.bowler_id,
          team_id: teamId,
          preferred_team_number: selectedTeamNumber,
          team_manually_set: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user?.id)
        .select();

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error(`Failed to update preferences: ${updateError.message}`);
      }

      // If no rows were updated, the user doesn't have preferences yet, so insert
      if (!updateData || updateData.length === 0) {
        const { error: insertError } = await supabase
          .from('user_preferences')
          .insert({
            user_id: user?.id,
            selected_player_id: selectedPlayer.bowler_id,
            team_id: teamId,
            preferred_team_number: selectedTeamNumber,
            team_manually_set: true
          });

        if (insertError) {
          console.error('Insert error:', insertError);
          throw new Error(`Failed to create preferences: ${insertError.message}`);
        }
      }

      console.log('Successfully saved user preferences');

      // Update the selected player in the app state
      setSelectedPlayer(selectedPlayer);

      onComplete();
    } catch (err) {
      console.error('Error saving selections:', err);
      setError(err instanceof Error ? err.message : 'Failed to save selections');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Welcome to Bowling League!</h2>
          <p className="text-sm text-gray-600 mt-1">
            To get started, please select your player and team.
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6 max-h-96 overflow-y-auto">
          {/* Player Selection */}
          <div>
            <label htmlFor="player-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Your Player *
            </label>
            {playersLoading ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="sm" className="mr-2" />
                <span className="text-sm text-gray-500">Loading players...</span>
              </div>
            ) : playersError ? (
              <div className="text-sm text-red-600">Failed to load players</div>
            ) : (
              <select
                id="player-select"
                value={selectedPlayerId}
                onChange={(e) => setSelectedPlayerId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a player...</option>
                {players.map((player) => (
                  <option key={player.bowler_id} value={player.bowler_id.toString()}>
                    {player.bowler_name} - {player.team_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Team Selection */}
          <div>
            <label htmlFor="team-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Your Team *
            </label>
            {teamsLoading ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner size="sm" className="mr-2" />
                <span className="text-sm text-gray-500">Loading teams...</span>
              </div>
            ) : (
              <select
                id="team-select"
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a team...</option>
                {teams.map((team) => (
                  <option key={team.team_id} value={team.team_id.toString()}>
                    {team.team_name} (#{team.team_number})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Info Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              You can change these selections later in Settings if needed.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={handleSave}
            disabled={!selectedPlayerId || !selectedTeamId || saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              'Get Started'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}